import { useState, useEffect } from 'react'
import {
	ShieldCheck,
	Zap,
	ArrowRight,
	UserPlus,
	Mail,
	Heart,
} from 'lucide-react'
import { Link } from 'react-router-dom'

const AuthRequiredScreen = () => {
	const [isVisible, setIsVisible] = useState(false)
	const [currentIcon, setCurrentIcon] = useState(0)
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

	const floatingIcons = [
		{ Icon: ShieldCheck, delay: 0, color: 'text-red-500' },
		{ Icon: Zap, delay: 1000, color: 'text-gray-700' },
		{ Icon: Heart, delay: 2000, color: 'text-red-600' },
		{ Icon: Mail, delay: 3000, color: 'text-gray-600' },
	]

	useEffect(() => {
		setIsVisible(true)
		const interval = setInterval(() => {
			setCurrentIcon((prev) => (prev + 1) % floatingIcons.length)
		}, 3000)
		return () => clearInterval(interval)
	}, [])

	useEffect(() => {
		const handleMouseMove = (e: any) => {
			setMousePosition({
				x: (e.clientX / window.innerWidth) * 100,
				y: (e.clientY / window.innerHeight) * 100,
			})
		}
		window.addEventListener('mousemove', handleMouseMove)
		return () => window.removeEventListener('mousemove', handleMouseMove)
	}, [])

	return (
		<div className='h-auto pt-8 bg-white relative overflow-hidden flex items-center justify-center'>
			<div
				className='absolute inset-0 opacity-[0.02] transition-all duration-1000'
				style={{
					background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, #ef4444 0%, #374151 50%, transparent 70%)`,
				}}
			/>

			<div className='absolute top-20 left-20 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-40' />
			<div className='absolute bottom-32 right-32 w-1 h-1 bg-gray-700 rounded-full animate-bounce opacity-60' />
			<div className='absolute top-1/3 right-20 w-4 h-[1px] bg-red-400 animate-pulse' />
			<div className='absolute bottom-1/3 left-16 w-[1px] h-6 bg-gray-600 animate-pulse delay-500' />

			{floatingIcons.map((item, index) => {
				const { Icon } = item
				return (
					<div
						key={index}
						className={`absolute opacity-5 ${
							currentIcon === index
								? 'animate-pulse scale-110'
								: 'animate-bounce'
						}`}
						style={{
							top: `${20 + index * 15}%`,
							right: `${10 + index * 8}%`,
							animationDelay: `${item.delay}ms`,
						}}
					>
						<Icon className={`w-8 h-8 ${item.color}`} />
					</div>
				)
			})}

			<div
				className={`relative z-10 max-w-full mx-auto px-6 text-center transition-all duration-1000 ${
					isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
				}`}
			>
				<div className='mb-8'>
					<div className='relative inline-block mb-6 group'>
						<div className='absolute inset-0 bg-gradient-to-r from-red-500 to-gray-800 rounded-full blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500 animate-pulse' />

						<div className='relative bg-gradient-to-br from-white via-red-600 to-red-700 p-6 rounded-full shadow-2xl transform group-hover:scale-105 transition-all duration-300 flex items-center justify-center'>
							<img
								src='/logo.png'
								alt='SportCenter Logo'
								className='w-12 h-12 md:w-14 md:h-14 object-contain drop-shadow-lg'
							/>
						</div>

						<div className='absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg animate-bounce delay-300'>
							<ShieldCheck className='w-4 h-4 text-red-500' />
						</div>
						<div className='absolute -bottom-2 -left-2 bg-white rounded-full p-2 shadow-lg animate-bounce delay-700'>
							<Zap className='w-4 h-4 text-gray-700' />
						</div>
					</div>

					<h1 className='text-5xl md:text-6xl lg:text-7xl font-black mb-4 tracking-tight leading-none text-center'>
						<span className='bg-gradient-to-r from-gray-900 via-black to-gray-800 bg-clip-text text-transparent block'>
							SportCenter
						</span>
						<span className='bg-gradient-to-r from-red-500 via-red-600 to-red-700 bg-clip-text text-transparent block -mt-2'>
							Shop
						</span>
					</h1>
				</div>

				<div className='relative group mb-8 w-full'>
					<div className='absolute inset-0 bg-gradient-to-r from-red-500 to-gray-700 rounded-3xl blur-xl opacity-10 group-hover:opacity-20 transition-opacity duration-500' />

					<div className='relative w-full bg-white border-2 border-gray-100 rounded-3xl p-8 lg:p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:border-gray-200 hover:scale-[1.01]'>
						<div className='flex items-center justify-center mb-6'>
							<div className='flex items-center space-x-3 bg-gray-50 px-6 py-3 rounded-full'>
								<div className='relative'>
									<div className='w-3 h-3 bg-red-500 rounded-full animate-pulse' />
									<div className='absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping opacity-40' />
								</div>
								<span className='text-sm font-semibold text-gray-700 tracking-wider uppercase'>
									Autenticación Requerida
								</span>
							</div>
						</div>

						<div className='space-y-4 mb-8'>
							<h2 className='text-3xl md:text-4xl font-bold text-gray-900 leading-tight'>
								¡Hola de nuevo!
							</h2>

							<div className='space-y-3'>
								<p className='text-lg text-gray-600 leading-relaxed'>
									Para acceder a tu mundo de compras personalizado,
								</p>
								<p className='text-xl font-semibold text-gray-900'>
									necesitamos verificar que eres
									<span className='relative mx-2'>
										<span className='bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent'>
											tú
										</span>
										<div className='absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-red-700 animate-pulse' />
									</span>
								</p>
							</div>

							<div className='flex items-center justify-center space-x-2 text-gray-500 text-sm mt-6'>
								<span>Es rápido</span>
								<div className='w-1 h-1 bg-gray-400 rounded-full animate-pulse' />
								<span>Es seguro</span>
								<div className='w-1 h-1 bg-gray-400 rounded-full animate-pulse delay-300' />
								<span>Es fácil</span>
							</div>
						</div>

						<div className='flex flex-col sm:flex-row gap-4 justify-center'>
							<Link
								to='/Login'
								className='group relative bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-10 py-5 rounded-2xl font-semibold text-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl overflow-hidden flex items-center justify-center'
							>
								<div className='absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700' />
								<span className='relative flex items-center'>
									Iniciar Sesión
									<ArrowRight className='w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300' />
								</span>
							</Link>

							<Link
								to='/Registro'
								className='group border-2 border-gray-300 hover:border-gray-800 text-gray-700 hover:text-black px-10 py-5 rounded-2xl font-semibold text-lg transition-all duration-300 transform hover:scale-[1.02] hover:bg-gray-50 flex items-center justify-center'
							>
								<UserPlus className='w-5 h-5 mr-3 group-hover:rotate-12 transition-transform duration-300' />
								Crear Cuenta Nueva
							</Link>
						</div>

						<div className='flex items-center justify-center space-x-8 mt-8 pt-6 border-t border-gray-100'>
							<div className='flex items-center space-x-2 text-sm text-gray-500'>
								<div className='w-2 h-2 bg-green-500 rounded-full animate-pulse' />
								<span>Conexión Segura</span>
							</div>
							<div className='flex items-center space-x-2 text-sm text-gray-500'>
								<ShieldCheck className='w-4 h-4 text-blue-500 animate-pulse' />
								<span>Datos Protegidos</span>
							</div>
							<div className='flex items-center space-x-2 text-sm text-gray-500'>
								<Heart className='w-4 h-4 text-red-500 animate-pulse' />
								<span>Confianza Total</span>
							</div>
						</div>
					</div>
				</div>

				<div className='text-center space-y-3'>
					<div className='flex items-center justify-center space-x-2 text-xs text-gray-400'>
						<span>© 2025 SportCenter Shop</span>
						<div className='w-1 h-1 bg-gray-300 rounded-full animate-pulse' />
						<span>Seguro & Confiable</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export default AuthRequiredScreen
